import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import TagManager from 'react-gtm-module';

import TSDrawer from '@ts-core/components/Drawer/TSDrawer';
import UserMenu from './UserMenu';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';

import { CourseStatus } from '../../utils/Constants';

const NavigationBar = (props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { user, currentCourse, courseMenuItems, topMenuItems, onSignOut, onOpenSettings, profileUrl, children, CourseStateComponent } = props;

  let { academy } = user;
  if (!academy) {
    academy = currentCourse?.academy || {};
  }

  useEffect(() => {
    const tagManagerArgs = {
      dataLayer: {
        event: 'changeCourse',
        course: currentCourse?.name,
        courseId: currentCourse?._id,
        academy: academy?.name,
        academyId: academy?._id,
      },
    };
    TagManager.dataLayer(tagManagerArgs);
  }, [academy, currentCourse]);

  const handleOpenMenu = useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  return (
    <Box width="100%" height={{ xs: 56, lg: 64 }}>
      <AppBar position="fixed" color="primary" elevation={1}>
        <div className="branch-journeys-top" />
        <Toolbar>
          <IconButton sx={{ ml: -1.5, mr: 2.5 }} color="inherit" aria-label="Open drawer" onClick={handleOpenMenu} size="large">
            <MenuIcon />
          </IconButton>
          <Box display="flex" flexDirection="column">
            {/* <Typography variant="h6" component="p" color="inherit" noWrap> */}
            <Link
              fontSize="h6.fontSize"
              fontWeight="h6.fontWeight"
              noWrap
              component={RouterLink}
              color="inherit"
              sx={{ '&:hover': { opacity: 0.8 }, transition: 'opacity 0.2s ease-in-out' }}
              to={currentCourse?._id ? `/${currentCourse._id}/dashboard` : '/courses'}
            >
              {currentCourse?.name || academy?.name || 'Check'}
            </Link>
            {/* </Typography> */}
            <Typography variant="caption">{academy.name}</Typography>
          </Box>
          <RenderCourseState CourseStateComponent={CourseStateComponent} currentCourse={currentCourse} />
          <Box flexGrow={1} />
          <Stack direction="row" spacing={1} alignItems="center">
            {children}
            {onOpenSettings && (
              <IconButton color="inherit" onClick={onOpenSettings} size="large">
                <SettingsIcon />
              </IconButton>
            )}
            <UserMenu user={user} onSignOut={onSignOut} profileUrl={profileUrl} />
          </Stack>
        </Toolbar>
      </AppBar>
      <TSDrawer
        isOpen={isMenuOpen}
        course={currentCourse}
        user={user}
        courseMenuItems={courseMenuItems}
        topMenuItems={topMenuItems}
        onClose={handleCloseMenu}
      />
    </Box>
  );
};

const RenderCourseState = ({ CourseStateComponent, currentCourse }) => {
  if (!CourseStateComponent || !currentCourse || Object.keys(currentCourse).length === 0) {
    return null;
  }

  return <CourseStateComponent isActive={currentCourse.periodStatus === CourseStatus.Active} />;
};

RenderCourseState.propTypes = {
  CourseStateComponent: PropTypes.func,
};

RenderCourseState.defaultProps = {
  CourseStateComponent: undefined,
};

NavigationBar.propTypes = {
  children: PropTypes.node,
  user: PropTypes.object,
  topMenuItems: PropTypes.array.isRequired,
  currentCourse: PropTypes.object,
  courseMenuItems: PropTypes.array,
  profileUrl: PropTypes.string.isRequired,

  onSignOut: PropTypes.func.isRequired,
  onOpenSettings: PropTypes.func,
  ...RenderCourseState.propTypes,
};

NavigationBar.defaultProps = {
  user: {},
  currentCourse: undefined,
  courseMenuItems: [],
  onOpenSettings: undefined,
};

export default NavigationBar;
